import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import ConfirmModal from "../components/ConfirmModal";

// Reports that app users filed against posts, comments and users. An admin removes the
// content or deactivates the user with the existing admin endpoints, then resolves the report.

const REASON_LABELS = {
  spam: "Spam",
  harassment: "Harassment or hate",
  nudity: "Nudity or sexual content",
  violence: "Violence or dangerous acts",
  other: "Something else",
};

const cell = { padding: "12px 14px", borderBottom: "1px solid #eee", verticalAlign: "top", fontSize: 14 };
const head = { ...cell, textAlign: "left", fontWeight: 600, color: "#555", background: "#fafafa" };
const btn = (bg, color = "#fff") => ({
  background: bg, color, border: "none", borderRadius: 6, padding: "6px 10px",
  fontSize: 13, cursor: "pointer", marginRight: 6, marginBottom: 6,
});

const Reports = () => {
  const Api = process.env.REACT_APP_BACKEND_URL || "";
  const baseUrl = Api.endsWith("/") ? Api : `${Api}/`;
  const [status, setStatus] = useState("open");
  const [reports, setReports] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // { title, message, run } for actions that remove content or deactivate a user
  const [confirm, setConfirm] = useState(null);

  const request = useCallback(async (path, options = {}) => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || data.message || "Request failed");
    }
    return data;
  }, [baseUrl]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request(`admin/reports?status=${status}&limit=200`);
      setReports(data.reports || []);
      setOpenCount(data.open_count || 0);
    } catch (error) {
      toast.error(error.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [request, status]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (report, quiet = false) => {
    await request(`admin/reports/${report.id}/resolve`, { method: "PUT" });
    if (!quiet) toast.success("Report resolved");
  };

  // Run an action for one report, then resolve it and refresh the list.
  const act = async (report, action, successMessage) => {
    setBusyId(report.id);
    try {
      if (action) await action();
      await resolve(report, !!action);
      if (action) toast.success(successMessage);
      await load();
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const mediaSrc = (path) => `${baseUrl}${String(path).replace(/^\/+/, "")}`;

  const renderTarget = (r) => {
    if (!r.target?.exists) return <em style={{ color: "#999" }}>Already removed</em>;
    if (r.target_type === "post") {
      return (
        <div>
          {r.target.media_url && r.target.media_type?.startsWith("image/") && (
            <img src={mediaSrc(r.target.media_url)} alt="Reported post" style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 6, display: "block", marginBottom: 6 }} />
          )}
          {r.target.media_url && r.target.media_type?.startsWith("video/") && (
            <a href={mediaSrc(r.target.media_url)} target="_blank" rel="noreferrer">Open video</a>
          )}
          <div>{r.target.caption || <em style={{ color: "#999" }}>No caption</em>}</div>
        </div>
      );
    }
    if (r.target_type === "comment") return <div>“{r.target.comment_text}”</div>;
    return <div>User account</div>;
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>Reports</h2>
          <p style={{ margin: "4px 0 0", color: "#666" }}>{openCount} open {openCount === 1 ? "report" : "reports"}</p>
        </div>
        <div>
          {["open", "resolved", "all"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} style={btn(status === s ? "#111" : "#eee", status === s ? "#fff" : "#333")}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, overflowX: "auto", border: "1px solid #eee" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={head}>Reported</th>
              <th style={head}>Type</th>
              <th style={head}>Reason</th>
              <th style={head}>Content</th>
              <th style={head}>Reported user</th>
              <th style={head}>Reported by</th>
              <th style={head}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={cell} colSpan={7}>Loading…</td></tr>
            ) : reports.length === 0 ? (
              <tr><td style={cell} colSpan={7}>No {status === "all" ? "" : status} reports.</td></tr>
            ) : reports.map((r) => {
              const busy = busyId === r.id;
              const isOpen = r.status === "open";
              return (
                <tr key={r.id}>
                  <td style={cell}>{r.created_at ? new Date(r.created_at.endsWith("Z") ? r.created_at : `${r.created_at}Z`).toLocaleString() : "-"}</td>
                  <td style={{ ...cell, textTransform: "capitalize" }}>{r.target_type}</td>
                  <td style={cell}>
                    {REASON_LABELS[r.reason] || r.reason}
                    {r.details ? <div style={{ color: "#666", marginTop: 4 }}>{r.details}</div> : null}
                  </td>
                  <td style={{ ...cell, maxWidth: 280 }}>{renderTarget(r)}</td>
                  <td style={cell}>
                    {r.reported_user ? (
                      <>
                        <div>@{r.reported_user.user_name}</div>
                        <div style={{ color: "#666" }}>{r.reported_user.email}</div>
                        {!r.reported_user.is_active && <div style={{ color: "#c00" }}>Deactivated</div>}
                      </>
                    ) : "-"}
                  </td>
                  <td style={cell}>{r.reporter ? `@${r.reporter.user_name}` : "-"}</td>
                  <td style={cell}>
                    {!isOpen ? (
                      <span style={{ color: "#2e7d32" }}>Resolved</span>
                    ) : (
                      <>
                        {r.target_type === "post" && r.target?.exists && (
                          <button disabled={busy} style={btn("#d32f2f")} onClick={() => setConfirm({
                            title: "Delete this post?",
                            message: "The post is removed for everyone and the report is resolved. This cannot be undone.",
                            run: () => act(r, () => request(`admin/posts/${r.target_id}`, { method: "DELETE" }), "Post deleted"),
                          })}>Delete post</button>
                        )}
                        {r.target_type === "comment" && r.target?.exists && (
                          <button disabled={busy} style={btn("#d32f2f")} onClick={() => setConfirm({
                            title: "Delete this comment?",
                            message: "The comment is removed for everyone and the report is resolved. This cannot be undone.",
                            run: () => act(r, () => request(`admin/comments/${r.target_id}`, { method: "DELETE" }), "Comment deleted"),
                          })}>Delete comment</button>
                        )}
                        {r.reported_user?.is_active && (
                          <button disabled={busy} style={btn("#f57c00")} onClick={() => setConfirm({
                            label: "Deactivate",
                            title: `Deactivate @${r.reported_user.user_name}?`,
                            message: "They won't be able to use the app until reactivated in User Management. The report is resolved.",
                            run: () => act(r, () => request(`admin/users/${r.reported_user.id}/deactivate`, { method: "PUT" }), "User deactivated"),
                          })}>Deactivate user</button>
                        )}
                        <button disabled={busy} style={btn("#eee", "#333")} onClick={() => act(r)}>
                          {busy ? "Working…" : "Dismiss"}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm?.run()}
        title={confirm?.title}
        message={confirm?.message}
        isDeleting={!!busyId}
        confirmLabel={confirm?.label}
      />
    </div>
  );
};

export default Reports;
